import React, { useCallback, ReactNode } from 'react'
import { useAppSelector } from 'reduxTyped'
import { Icon, palettes, l, sanitizeURL } from '@manychat/manyui'

import { KeywordPreview } from 'apps/keywords/components/KeywordPreview/KeywordPreview'
import { Keyword } from 'apps/keywords/keywordsInterfaces'
import ProtectionStatus from 'apps/templates/components/ProtectionStatus'
import { ITemplateAccessData, TemplateApp } from 'apps/templates/constants/templatesInterfaces'
import { GroupId } from 'apps/templates/models/ContentMap/constants'
import { getPageId } from 'apps/templates/selectors/currentTemplateSelectors'
import { getDraft } from 'apps/templates/selectors/templateDraftSelectors'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import { Accordion, AccordionItem } from 'components/Accordion/Accordion'
import If from 'components/If'
import Tip from 'components/Tip'

export interface SubItemMainStructure {
  name: string
  id?: string
  status: string
  description?: string
  is_pro?: boolean
  has_fb_channel?: boolean
  has_ig_channel?: boolean
  has_tg_channel?: boolean
  has_sms_channel?: boolean
  has_wa_channel?: boolean
  is_locked_flow?: boolean
  keyword_rules?: Keyword['keyword_rules']
}

interface SubItem extends SubItemMainStructure {
  name: string
  status: TemplateApp['status']
  is_pro: boolean
  is_ai_addon?: boolean
  description: string | undefined
}

interface Item extends AccordionItem<SubItem> {
  groupId?: string
}

type ApplicationItem = AccordionItem<SubItem | ReactNode>

const isSubItemObject = (subItem: SubItem | React.ReactNode): subItem is SubItem =>
  typeof subItem === 'object' && subItem !== null && 'name' in subItem

const getToolsItem = ({
  appLabel,
  pageId,
  apps,
}: {
  appLabel: string
  pageId: string
  apps?: TemplateApp[]
}): AccordionItem<SubItem> => {
  const MaxDescriptionLength = 200
  return {
    label: appLabel,
    defaultOpen: Boolean(pageId),
    subItems:
      apps?.map(({ title, description, status }) => ({
        name: title,
        status,
        is_pro: true,
        description:
          (description ?? '').length > MaxDescriptionLength
            ? description?.slice(0, MaxDescriptionLength - 3) + '...'
            : description,
      })) ?? [],
  }
}

const getKeywordIcon = (keyword: SubItemMainStructure) => {
  switch (true) {
    case keyword.has_fb_channel:
      return Icon.MessengerChannel
    case keyword.has_ig_channel:
      return Icon.InstagramChannel
    case keyword.has_tg_channel:
      return Icon.TelegramChannel
    case keyword.has_sms_channel:
      return Icon.SmsChannel
    case keyword.has_wa_channel:
      return Icon.WhatsAppChannel
    default:
      return null
  }
}

const TemplateStructure = ({
  accessData,
  apps,
  hideProLabels = false,
  items,
  isInstalledTemplate,
  isTemplateInstallation,
  isEditTemplate,
}: {
  accessData?: ITemplateAccessData
  apps?: TemplateApp[]
  hideProLabels?: boolean
  items?: Item[]
  isInstalledTemplate?: boolean
  isTemplateInstallation?: boolean
  isEditTemplate?: boolean
}) => {
  const currentAccountID = useAppSelector(getCurrentAccountID)
  const currentPageId = useAppSelector(getPageId)
  const draftPageId = useAppSelector(getDraft).pageId
  const pageId = isEditTemplate ? currentPageId : draftPageId
  const settingsAppsURL = `/fb${pageId}/settings#apps`

  const renderChild = useCallback(
    (subItem: SubItem | React.ReactNode, item?: Item | ApplicationItem) => {
      if (!isSubItemObject(subItem)) return subItem

      const isTemplateSupportsLockedFlows = item?.subItems.every(
        (subItem) => isSubItemObject(subItem) && 'is_locked_flow' in subItem,
      )

      const isAllSubItemsLocked = item?.subItems.every(
        (subItem) => isSubItemObject(subItem) && subItem.is_locked_flow,
      )

      const isDeprecated =
        subItem?.status === 'deprecated' && !isInstalledTemplate && !isTemplateInstallation
      const IconComponent = getKeywordIcon(subItem)
      const LockIconComponent = subItem.is_locked_flow ? Icon.Protect : Icon.Unprotect

      const flowLockedIconTooltipText = subItem.is_locked_flow
        ? l.translate(
            'This item is a part of a locked Template which means it is locked from viewing and editing by the Template owner',
          )
        : l.translate(
            'This item is a part of the protected Template which means it cannot be shared, copied to another page, or included into a new template',
          )

      return (
        <span className="d-flex justify-between align-center">
          <span className="d-flex align-center">
            {accessData?.is_locked && isTemplateSupportsLockedFlows && !isAllSubItemsLocked && (
              <LockIconComponent
                data-title={flowLockedIconTooltipText}
                size={20}
                className="m-r-xxs"
              />
            )}

            {subItem.id?.startsWith('kw') &&
              IconComponent &&
              React.createElement(IconComponent, { className: 'm-r-xs', size: 20 })}

            <span className={isDeprecated ? 'text-secondary' : ''}>
              {'keyword_rules' in subItem && Array.isArray(subItem.keyword_rules) ? (
                <KeywordPreview keywordRules={subItem.keyword_rules} />
              ) : (
                subItem.name
              )}
            </span>

            <If condition={!hideProLabels && subItem.is_pro}>
              <span className="pro-label m-l-xs" />
            </If>
            <If condition={!hideProLabels && subItem.is_ai_addon}>
              <span className="ai-label m-l-xxs" />
            </If>

            {Boolean(subItem?.description) && (
              <Icon.Question
                data-title={subItem.description}
                className="m-l-xxs"
                color={palettes.typography.disabledFG}
                size={20}
              />
            )}
          </span>

          {isDeprecated && (
            <a
              className="d-flex text-primary"
              href={sanitizeURL(settingsAppsURL)}
              target="_blank"
              rel="noreferrer"
            >
              <Icon.Warning className="m-r-xxs" size={22} />
              {l.translate('Update to a recent version')}
            </a>
          )}
        </span>
      )
    },
    [accessData, hideProLabels, isInstalledTemplate, isTemplateInstallation, settingsAppsURL],
  )

  const appLabel = apps?.length === 1 ? 'App' : 'Apps'
  const renderItem = useCallback(
    (item: Item | ApplicationItem, isOpen: boolean) => {
      const numberOfItems = item?.subItems?.length ?? 0
      const showProLabel =
        !hideProLabels &&
        !isOpen &&
        Boolean(numberOfItems) &&
        item.subItems?.some((subItem) => {
          const isTemplateStructure = subItem && typeof subItem === 'object' && 'is_pro' in subItem
          return isTemplateStructure && subItem.is_pro
        })

      const showAiLabel =
        !hideProLabels &&
        !isOpen &&
        Boolean(numberOfItems) &&
        item.subItems?.some((subItem) => {
          const isTemplateStructure =
            subItem && typeof subItem === 'object' && 'is_ai_addon' in subItem
          return isTemplateStructure && subItem.is_ai_addon
        })

      const showUpdateRequired =
        item.label === appLabel &&
        apps?.some((el) => el.status === 'deprecated') &&
        !isOpen &&
        !isTemplateInstallation &&
        !isInstalledTemplate

      const isFlowsItem =
        ('groupId' in item && item.groupId === GroupId.FLOWS) || item.label === 'Flows'

      const isTemplateSupportsLockedFlows = item?.subItems.every(
        (subItem) => isSubItemObject(subItem) && 'is_locked_flow' in subItem,
      )

      const isAllSubItemsLocked = item?.subItems.every(
        (subItem) => isSubItemObject(subItem) && subItem.is_locked_flow,
      )

      const showProtectionTitle = isTemplateSupportsLockedFlows ? isAllSubItemsLocked : true

      return (
        <span className="d-flex justify-between align-center">
          <span className="d-flex align-center">
            <If condition={item.label !== 'General'}>
              <ProtectionStatus
                showTitle={isFlowsItem ? showProtectionTitle : true}
                className="m-r-xs"
                accessData={accessData}
              />
            </If>

            <span className="d-inline-block p-y-xs">
              {item.label === appLabel ? numberOfItems - 1 : numberOfItems} {item.label}
              {showProLabel && <span className="pro-label m-l" />}
              {showAiLabel && <span className="ai-label m-l-xxs" />}
            </span>
          </span>

          {showUpdateRequired && (
            <a
              className="d-flex text-primary"
              href={sanitizeURL(settingsAppsURL)}
              target="_blank"
              rel="noreferrer"
            >
              <Icon.Warning className="m-r-xxs" size={22} />
              {l.translate('Update required')}
            </a>
          )}
        </span>
      )
    },
    [
      accessData,
      appLabel,
      hideProLabels,
      apps,
      isTemplateInstallation,
      isInstalledTemplate,
      settingsAppsURL,
    ],
  )

  const toolsItem: ApplicationItem = getToolsItem({
    appLabel,
    pageId,
    apps,
  })
  const toolsItemHasApps = Boolean(toolsItem?.subItems?.length)

  if (toolsItemHasApps) {
    const text = isInstalledTemplate
      ? l.translate(
          'To use this template, please set up {name} listed below. Navigate to the App section, click on the app to open its settings, and follow the app’s developer recommendations.<br /><a class="text-primary" href="{href}" target="_blank" rel="noopener noreferrer">Go to Apps</a>',
          {
            name: toolsItem.subItems.length > 1 ? 'apps' : 'the app',
            href: `/${currentAccountID}/settings#apps`,
          },
        )
      : l.translate(
          'The most recent version of the app will be installed automatically along with the template.',
        )

    toolsItem.subItems?.unshift(<Tip text={text} rounded useInnerHTML />)
  }

  return (
    <div>
      {toolsItemHasApps && (
        <>
          <span>{l.translate('Tools')}</span>
          <Accordion items={[toolsItem]} renderItem={renderItem} renderChild={renderChild} />
          <hr className="m-b-md" />
        </>
      )}
      {items ? (
        <>
          {toolsItemHasApps && <span>{l.translate('Content')}</span>}
          <Accordion items={items} renderItem={renderItem} renderChild={renderChild} />
        </>
      ) : null}
    </div>
  )
}

export default TemplateStructure
